import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  SparklesIcon, 
  HeartIcon, 
  AcademicCapIcon, 
  UserGroupIcon,
  GlobeAltIcon,
  TrophyIcon 
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const About = () => {
  const { t } = useTranslation();
  const values = [
    { nameKey: 'about.values.craftsmanship', descKey: 'about.values.craftsmanshipDesc', icon: SparklesIcon },
    { nameKey: 'about.values.mentorship', descKey: 'about.values.mentorshipDesc', icon: UserGroupIcon },
    { nameKey: 'about.values.lifetime', descKey: 'about.values.lifetimeDesc', icon: HeartIcon },
    { nameKey: 'about.values.practical', descKey: 'about.values.practicalDesc', icon: AcademicCapIcon },
    { nameKey: 'about.values.global', descKey: 'about.values.globalDesc', icon: GlobeAltIcon },
    { nameKey: 'about.values.recognition', descKey: 'about.values.recognitionDesc', icon: TrophyIcon },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Kamal Institute",
    "description": "Premier institute for goldsmithing and silversmithing education. We offer immersive, fully residential programs with expert mentorship and lifetime support.",
    "url": "https://kamalinstitute.co.in",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Ajmer",
      "addressRegion": "Rajasthan",
      "addressCountry": "IN"
    },
    "founder": {
      "@type": "Person",
      "name": "Kamal Institute"
    },
    "knowsAbout": [
      "Goldsmithing",
      "Silversmithing",
      "Handmade Jewellery Making",
      "Jewellery Design",
      "Stone Setting",
      "Diamond Setting"
    ]
  };

  return (
    <div className="bg-white pb-16">
      <SEO
        title="About Kamal Institute - Premier Jewellery Making Education"
        description="Learn about Kamal Institute's mission, values, and commitment to excellence in goldsmithing and silversmithing education. Where tradition meets excellence in jewellery craftsmanship."
        keywords="about Kamal Institute, jewellery institute India, goldsmithing education, silversmithing training, handmade jewellery institute, Ajmer jewellery school"
        url="/about"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-gold-100/20 py-16 pt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {t('about.title')}
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
            {t('about.ourMission')}
          </h2>
          <div className="text-lg leading-8 text-gray-700 space-y-4">
            <p>{t('about.missionP1')}</p>
            <p>{t('about.missionP2')}</p>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {t('about.coreValues')}
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            {t('about.coreValuesDesc')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {values.map((value) => (
            <div key={value.nameKey} className="relative bg-gray-50 rounded-2xl p-8">
              <div className="flex items-center gap-x-3 mb-4">
                <value.icon className="h-8 w-8 text-gold-600" aria-hidden="true" />
                <h3 className="text-xl font-semibold text-gray-900">{t(value.nameKey)}</h3>
              </div>
              <p className="text-base leading-7 text-gray-600">{t(value.descKey)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* What Makes Us Different */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
        <div className="rounded-3xl bg-gray-900 px-6 py-16 sm:px-16 text-white">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-8">
              {t('about.whatMakesDifferent')}
            </h2>
            <div className="space-y-6 text-lg leading-8 text-gray-300">
              <p>
                <span className="font-semibold text-gold-400">{t('about.handmadeCraftsmanship')}</span> {t('about.handmadeText')}
              </p>
              <p>
                <span className="font-semibold text-gold-400">{t('about.lifetimeMentorship')}</span> {t('about.lifetimeText')}
              </p>
              <p>
                <span className="font-semibold text-gold-400">{t('about.immersiveExperience')}</span> {t('about.immersiveText')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Location Section */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
        <div className="mx-auto max-w-2xl text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {t('about.locatedIn')}
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            {t('about.locatedDesc')}
          </p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
        <div className="rounded-3xl bg-gradient-to-r from-gold-600 to-gold-500 px-6 py-16 text-center sm:px-16">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t('about.joinCommunity')}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gold-50">
            {t('about.joinDesc')}
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Link
              to="/courses"
              className="rounded-md bg-white px-6 py-3 text-base font-semibold text-gold-600 shadow-sm hover:bg-gold-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all"
            >
              {t('about.viewCourses')}
            </Link>
            <Link
              to="/contact"
              className="text-base font-semibold leading-6 text-white hover:text-gold-100 transition-colors"
            >
              {t('about.getInTouch')} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
